import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
    useGetMusicBotOverviewQuery,
    useGetMusicBotUsersQuery,
    useGetMusicBotLinksQuery,
    useGetMusicBotEventsQuery,
    useGetMusicBotPostsQuery,
    useCreateMusicBotPostMutation,
    useUpdateMusicBotPostMutation,
    useDeleteMusicBotPostMutation,
    useHideMusicBotPostMutation,
    useUploadMusicBotPostMediaMutation,
    usePublishMusicBotPostMutation,
    useSyncMusicBotPostMutation,
    useImportMusicBotPostsMutation,
} from "../api";
import type { MusicChannelPost } from "../api/musicBot";
import {
    toTelegramHtml,
    telegramHtmlToPreviewHtml,
    telegramHtmlToEditorHtml,
    telegramHtmlToText,
    telegramLimitStats,
    TG_ENTITY_LIMIT,
    TG_PHOTO_MAX_BYTES,
    TG_VIDEO_MAX_BYTES,
} from "../utils/telegramHtml";

type MainTab = "post" | "stats" | "history";

/** обкладинка через Laravel (Bearer) — CDN telesco у <img> мертвий */
const AuthCover: React.FC<{
    postId: number;
    className?: string;
    refCover?: boolean;
}> = ({ postId, className, refCover }) => {
    const [src, setSrc] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let objectUrl: string | null = null;
        let cancelled = false;
        setSrc(null);
        setFailed(false);
        (async () => {
            try {
                const base = process.env.REACT_APP_API_BASE_URL || "/api/v1";
                // токен у Cookies (як у AuthInitializer), не в localStorage
                const headers: Record<string, string> = {
                    Accept: "application/json, image/*",
                };
                const token =
                    Cookies.get("token") || localStorage.getItem("token");
                const apiKey = process.env.REACT_APP_API_KEY;
                if (token) headers.Authorization = `Bearer ${token}`;
                if (apiKey) headers.apiKey = apiKey;
                const q = refCover ? "?ref=1" : "";
                const res = await fetch(
                    `${base}/admin/music-bot/posts/${postId}/cover${q}`,
                    { headers },
                );
                if (!res.ok) throw new Error("no cover");
                const ctype = (
                    res.headers.get("content-type") || ""
                ).toLowerCase();
                if (ctype.includes("json") || ctype.includes("text/html")) {
                    throw new Error("not image");
                }
                const blob = await res.blob();
                if (cancelled) return;
                if (!blob.size || blob.type.includes("json"))
                    throw new Error("empty");
                objectUrl = URL.createObjectURL(blob);
                setSrc(objectUrl);
            } catch {
                if (!cancelled) setFailed(true);
            }
        })();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [postId, refCover]);

    if (failed) {
        return (
            <div
                className={`${className || ""} flex items-center justify-center bg-gray-100 text-[10px] text-gray-400 dark:bg-gray-900`}
            >
                нет фото
            </div>
        );
    }
    if (!src) {
        return (
            <div
                className={`${className || ""} animate-pulse bg-gray-100 dark:bg-gray-900`}
            />
        );
    }
    return <img src={src} alt="" className={className} />;
};

const CoverSlot: React.FC<{
    src?: string | null;
    postId?: number | null;
    className?: string;
    fallback?: React.ReactNode;
    refCover?: boolean;
}> = ({ src, postId, className, fallback, refCover }) => {
    // лише локальний blob-превʼю; http CDN telesco не використовувати
    if (src?.startsWith("blob:")) {
        return <img src={src} alt="" className={className} />;
    }
    if (postId)
        return (
            <AuthCover
                postId={postId}
                className={className}
                refCover={refCover}
            />
        );
    return <>{fallback ?? null}</>;
};
type StatsSub = "generated" | "referrals" | "users";

function StatCard({
    label,
    value,
    hint,
    tone = "plain",
}: {
    label: string;
    value: number;
    hint: string;
    tone?: "plain" | "green";
}) {
    const box =
        tone === "green"
            ? "border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/30"
            : "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800";
    const labelCls =
        tone === "green"
            ? "text-emerald-700 dark:text-emerald-400"
            : "text-gray-500";
    const valueCls =
        tone === "green"
            ? "text-emerald-900 dark:text-emerald-200"
            : "";
    const hintCls =
        tone === "green"
            ? "text-emerald-800/80 dark:text-emerald-300/80"
            : "text-gray-500";
    return (
        <div className={`rounded-xl border p-5 shadow-sm ${box}`}>
            <div className={`text-xs uppercase tracking-wide ${labelCls}`}>
                {label}
            </div>
            <div className={`mt-1 text-3xl font-semibold ${valueCls}`}>{value}</div>
            <div className={`mt-1 text-xs ${hintCls}`}>{hint}</div>
        </div>
    );
}

function tgLabel(u?: {
    username?: string | null;
    first_name?: string | null;
    tg_id?: number | null;
}): string {
    if (!u) return "—";
    if (u.username) return `@${u.username}`;
    if (u.first_name) return String(u.first_name);
    return u.tg_id ? `id:${u.tg_id}` : "—";
}

function fmtWhen(s?: string | null): string {
    if (!s) return "—";
    return s.replace("T", " ").slice(0, 16);
}

const Toolbar: React.FC<{ editor: ReturnType<typeof useEditor> }> = ({
    editor,
}) => {
    if (!editor) return null;
    const buttons = [
        {
            mark: "bold",
            title: "Жирный",
            label: <strong>B</strong>,
            action: () => editor.chain().focus().toggleBold().run(),
        },
        {
            mark: "italic",
            title: "Курсив",
            label: <em>I</em>,
            action: () => editor.chain().focus().toggleItalic().run(),
        },
        {
            mark: "strike",
            title: "Зачёркнутый",
            label: <s>S</s>,
            action: () => editor.chain().focus().toggleStrike().run(),
        },
        {
            mark: "code",
            title: "Моно",
            label: <code>{"</>"}</code>,
            action: () => editor.chain().focus().toggleCode().run(),
        },
        {
            mark: "link",
            title: "Ссылка",
            label: "🔗",
            action: () => {
                const url = window.prompt(
                    "URL:",
                    editor.getAttributes("link").href || "https://",
                );
                if (!url) return;
                editor.chain().focus().setLink({ href: url }).run();
            },
        },
        {
            mark: "",
            title: "Убрать ссылку",
            label: "🔗×",
            action: () => editor.chain().focus().unsetLink().run(),
        },
        {
            mark: "",
            title: "Сбросить",
            label: "Tx",
            action: () =>
                editor.chain().focus().unsetAllMarks().clearNodes().run(),
        },
    ];
    return (
        <div className="flex flex-wrap gap-1 border-b border-gray-200 bg-gray-50 p-1.5 dark:border-gray-600 dark:bg-gray-800">
            {buttons.map((b) => {
                const active = Boolean(b.mark) && editor.isActive(b.mark);
                return (
                    <button
                        key={b.title}
                        type="button"
                        title={b.title}
                        onClick={b.action}
                        className={`min-w-[2rem] rounded px-2 py-1 text-sm ${
                            active
                                ? "bg-blue-600 text-white"
                                : "border border-gray-300 bg-white hover:bg-gray-100 dark:border-gray-500 dark:bg-gray-800"
                        }`}
                    >
                        {b.label}
                    </button>
                );
            })}
        </div>
    );
};

const ChannelPreview: React.FC<{
    bodyHtml: string;
    photoUrl?: string | null;
    coverPostId?: number | null;
    videoName?: string | null;
    audioName?: string | null;
}> = ({ bodyHtml, photoUrl, coverPostId, videoName, audioName }) => {
    const previewHtml = telegramHtmlToPreviewHtml(bodyHtml);
    const plain = telegramHtmlToText(bodyHtml);
    const empty =
        !plain && !photoUrl && !coverPostId && !videoName && !audioName;
    return (
        <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0e1621] shadow-lg">
            <div className="flex items-center gap-2 border-b border-white/5 bg-[#17212b] px-3 py-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2b5278] text-xs font-semibold text-white">
                    ♪
                </div>
                <div>
                    <div className="text-sm font-semibold text-white">
                        @ALNY_art
                    </div>
                    <div className="text-[11px] text-sky-300/80">
                        пост в канале
                    </div>
                </div>
            </div>
            <div className="space-y-2 p-3">
                <CoverSlot
                    src={photoUrl}
                    postId={coverPostId}
                    className="max-h-64 w-full rounded-lg object-cover"
                />
                {videoName ? (
                    <div className="rounded-lg bg-[#182533] px-3 py-4 text-center text-xs text-[#8ea0b0]">
                        🎬 видео: {videoName}
                    </div>
                ) : null}
                {audioName ? (
                    <div className="rounded-lg bg-[#182533] px-3 py-4 text-center text-xs text-[#8ea0b0]">
                        🎵 аудио: {audioName}
                    </div>
                ) : null}
                <div className="rounded-lg bg-[#182533] px-3 py-2.5 text-[13.5px] leading-[1.45] text-[#e8f4ff]">
                    {empty ? (
                        <span className="italic text-[#6d7f8f]">
                            Подпись канала…
                        </span>
                    ) : (
                        <div
                            className="tg-preview break-words"
                            dangerouslySetInnerHTML={{ __html: previewHtml }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const RefPreview: React.FC<{
    title: string;
    contentHtml: string;
    photoUrl?: string | null;
    coverPostId?: number | null;
}> = ({ title, contentHtml, photoUrl, coverPostId }) => {
    const head =
        (title || "").trim().split(/\n+/).filter(Boolean)[0] || "Заголовок";
    const previewHtml = telegramHtmlToPreviewHtml(contentHtml);
    const plain = telegramHtmlToText(contentHtml);
    return (
        <div className="overflow-hidden rounded-xl border border-white/10 bg-[#16111f] shadow-lg">
            <div className="flex items-center gap-2 border-b border-violet-900/40 bg-[#1c1528] px-3 py-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-600 text-[10px] font-bold text-white">
                    AM
                </div>
                <div>
                    <div className="text-sm font-semibold text-white">
                        @alny_music_bot
                    </div>
                    <div className="text-[11px] text-violet-300/80">
                        реф-пост для пересылки
                    </div>
                </div>
            </div>
            <div className="p-3">
                <div className="mb-2 rounded-lg border border-dashed border-violet-500/30 bg-violet-950/30 px-3 py-2 text-[11px] text-violet-200/90">
                    Готово. Зажми → «Переслать»…
                    <div className="mt-1 text-center text-base">⬇️⬇️⬇️</div>
                </div>
                <div className="overflow-hidden rounded-lg bg-[#22182e]">
                    <CoverSlot
                        src={photoUrl}
                        postId={coverPostId}
                        refCover
                        className="max-h-56 w-full object-cover"
                        fallback={
                            <div className="flex h-20 items-center justify-center bg-[#140f1a] text-xs text-[#a08fb8]">
                                из канала (видео/фото)
                            </div>
                        }
                    />
                    <div className="space-y-2 px-3 py-2.5 text-[13.5px] text-[#f0e8ff]">
                        <div className="font-semibold">{head}</div>
                        {plain ? (
                            <div
                                className="tg-preview max-h-40 overflow-y-auto break-words text-[#d4c6ea]"
                                dangerouslySetInnerHTML={{
                                    __html: previewHtml,
                                }}
                            />
                        ) : (
                            <div className="italic text-[#8a7a9e]">
                                Контент…
                            </div>
                        )}
                        <div className="rounded-lg bg-[#3d2a5c] px-3 py-2 text-center text-sm text-white">
                            🎧 Перейти в канал
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const MusicBotPage: React.FC = () => {
    const [mainTab, setMainTab] = useState<MainTab>("post");
    const [editingId, setEditingId] = useState<number | null>(null);
    const [title, setTitle] = useState("");
    const [bodyHtml, setBodyHtml] = useState("");
    const [descHtml, setDescHtml] = useState("");
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [audioFile, setAudioFile] = useState<File | null>(null);
    const [refPhotoFile, setRefPhotoFile] = useState<File | null>(null);
    const [localPhotoUrl, setLocalPhotoUrl] = useState<string | null>(null);
    const [localRefPhotoUrl, setLocalRefPhotoUrl] = useState<string | null>(
        null,
    );
    const [videoName, setVideoName] = useState<string | null>(null);
    const [audioName, setAudioName] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [flash, setFlash] = useState<string | null>(null);
    const [err, setErr] = useState<string | null>(null);
    /** чернетки alias msg id по посту в Історії */
    const [aliasDrafts, setAliasDrafts] = useState<Record<number, string>>({});
    const [statsPeriod, setStatsPeriod] = useState<
        "day" | "week" | "month" | "all"
    >("all");
    const [statsSub, setStatsSub] = useState<StatsSub>("generated");
    const [offset, setOffset] = useState(0);
    const limit = 50;

    const bodyEditor = useEditor({
        extensions: [
            StarterKit.configure({
                link: { openOnClick: false, autolink: true, linkOnPaste: true },
            }),
        ],
        content: "",
        onUpdate: ({ editor: ed }) => setBodyHtml(ed.getHTML()),
    });
    const descEditor = useEditor({
        extensions: [
            StarterKit.configure({
                link: { openOnClick: false, autolink: true, linkOnPaste: true },
            }),
        ],
        content: "",
        onUpdate: ({ editor: ed }) => setDescHtml(ed.getHTML()),
    });

    const overviewQ = useGetMusicBotOverviewQuery(
        { period: statsPeriod },
        {
            pollingInterval: mainTab === "stats" ? 60000 : 0,
        },
    );
    const postsQ = useGetMusicBotPostsQuery(
        { limit: 300 },
        { pollingInterval: 30000 },
    );
    const usersQ = useGetMusicBotUsersQuery(
        { limit, offset },
        { skip: mainTab !== "stats" || statsSub !== "users" },
    );
    const linksQ = useGetMusicBotLinksQuery(
        { limit, offset },
        { skip: mainTab !== "stats" || statsSub !== "generated" },
    );
    const eventsQ = useGetMusicBotEventsQuery(
        { limit, offset, kind: "referral_attached" },
        { skip: mainTab !== "stats" || statsSub !== "referrals" },
    );

    const [createPost] = useCreateMusicBotPostMutation();
    const [updatePost] = useUpdateMusicBotPostMutation();
    const [deletePost] = useDeleteMusicBotPostMutation();
    const [hidePost] = useHideMusicBotPostMutation();
    const [uploadMedia] = useUploadMusicBotPostMediaMutation();
    const [publishPost] = usePublishMusicBotPostMutation();
    const [syncPost] = useSyncMusicBotPostMutation();
    const [importPosts] = useImportMusicBotPostsMutation();

    const overview = overviewQ.data?.data;
    const history = postsQ.data?.data || [];
    const editingPost = history.find((p) => p.id === editingId) || null;
    const editingPublished =
        editingPost?.status === "published" && !!editingPost.channel_message_id;

    const previewPhoto = localPhotoUrl;
    const coverPostId =
        previewPhoto ||
        !(editingPost?.has_cover || editingPost?.channel_message_id)
            ? null
            : editingPost.id;
    const hasCoverPreview = Boolean(previewPhoto || coverPostId);

    const refPreviewPhoto = localRefPhotoUrl;
    const refCoverPostId =
        refPreviewPhoto || !editingPost?.has_ref_cover
            ? null
            : editingPost.id;
    const hasRefCoverPreview = Boolean(refPreviewPhoto || refCoverPostId);

    const previewVideoName = videoName || editingPost?.video_name || null;
    const previewAudioName = audioName || editingPost?.audio_name || null;

    const channelLimits = telegramLimitStats(
        bodyHtml,
        Boolean(hasCoverPreview || previewVideoName || previewAudioName),
    );
    const refLimits = telegramLimitStats(descHtml, hasRefCoverPreview);

    useEffect(() => {
        return () => {
            if (localPhotoUrl?.startsWith("blob:"))
                URL.revokeObjectURL(localPhotoUrl);
        };
    }, [localPhotoUrl]);

    useEffect(() => {
        return () => {
            if (localRefPhotoUrl?.startsWith("blob:"))
                URL.revokeObjectURL(localRefPhotoUrl);
        };
    }, [localRefPhotoUrl]);

    const setEditorsContent = (body: string, desc: string) => {
        const b = telegramHtmlToEditorHtml(body || "");
        const d = telegramHtmlToEditorHtml(desc || "");
        bodyEditor?.commands.setContent(b || "", { emitUpdate: false });
        descEditor?.commands.setContent(d || "", { emitUpdate: false });
        setBodyHtml(b);
        setDescHtml(d);
    };

    const resetForm = () => {
        setEditingId(null);
        setTitle("");
        setPhotoFile(null);
        setVideoFile(null);
        setAudioFile(null);
        setRefPhotoFile(null);
        if (localPhotoUrl?.startsWith("blob:"))
            URL.revokeObjectURL(localPhotoUrl);
        if (localRefPhotoUrl?.startsWith("blob:"))
            URL.revokeObjectURL(localRefPhotoUrl);
        setLocalPhotoUrl(null);
        setLocalRefPhotoUrl(null);
        setVideoName(null);
        setAudioName(null);
        setEditorsContent("", "");
        setErr(null);
        setFlash(null);
    };

    const loadPost = (p: MusicChannelPost) => {
        setEditingId(p.id);
        setTitle(p.title || "");
        setPhotoFile(null);
        setVideoFile(null);
        setAudioFile(null);
        setRefPhotoFile(null);
        if (localPhotoUrl?.startsWith("blob:"))
            URL.revokeObjectURL(localPhotoUrl);
        if (localRefPhotoUrl?.startsWith("blob:"))
            URL.revokeObjectURL(localRefPhotoUrl);
        setLocalPhotoUrl(null);
        setLocalRefPhotoUrl(null);
        setVideoName(p.video_name || (p.has_video ? "видео" : null));
        setAudioName(p.audio_name || (p.has_audio ? "аудио" : null));
        setEditorsContent(p.body || "", p.description || "");
        setMainTab("post");
        setFlash(null);
        setErr(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const pickPhoto = (file: File | null) => {
        if (localPhotoUrl?.startsWith("blob:"))
            URL.revokeObjectURL(localPhotoUrl);
        if (!file) {
            setPhotoFile(null);
            setLocalPhotoUrl(null);
            return;
        }
        if (file.size > TG_PHOTO_MAX_BYTES) {
            setErr(
                `Фото больше ${Math.round(TG_PHOTO_MAX_BYTES / 1024 / 1024)} МБ`,
            );
            return;
        }
        setPhotoFile(file);
        setLocalPhotoUrl(URL.createObjectURL(file));
    };

    const pickRefPhoto = (file: File | null) => {
        if (localRefPhotoUrl?.startsWith("blob:"))
            URL.revokeObjectURL(localRefPhotoUrl);
        if (!file) {
            setRefPhotoFile(null);
            setLocalRefPhotoUrl(null);
            return;
        }
        if (file.size > TG_PHOTO_MAX_BYTES) {
            setErr(
                `Фото больше ${Math.round(TG_PHOTO_MAX_BYTES / 1024 / 1024)} МБ`,
            );
            return;
        }
        setRefPhotoFile(file);
        setLocalRefPhotoUrl(URL.createObjectURL(file));
    };

    const pickVideo = (file: File | null) => {
        if (!file) {
            setVideoFile(null);
            setVideoName(editingPost?.video_name || null);
            return;
        }
        if (file.size > TG_VIDEO_MAX_BYTES) {
            setErr(
                `Видео больше ${Math.round(TG_VIDEO_MAX_BYTES / 1024 / 1024)} МБ`,
            );
            return;
        }
        setVideoFile(file);
        setVideoName(file.name);
    };

    const pickAudio = (file: File | null) => {
        if (!file) {
            setAudioFile(null);
            setAudioName(editingPost?.audio_name || null);
            return;
        }
        if (file.size > TG_VIDEO_MAX_BYTES) {
            setErr("Аудио слишком большое (макс ~50 МБ)");
            return;
        }
        setAudioFile(file);
        setAudioName(file.name);
    };

    const persist = async (): Promise<number> => {
        const titleVal = title.trim();
        const bodyTg = toTelegramHtml(bodyEditor?.getHTML() ?? bodyHtml);
        const descTg = toTelegramHtml(descEditor?.getHTML() ?? descHtml);

        let id = editingId;
        if (!id) {
            const created = await createPost({
                title: titleVal,
                description: descTg,
                body: bodyTg,
            }).unwrap();
            id = created.data.id;
            setEditingId(id);
        } else {
            await updatePost({
                id,
                title: titleVal,
                description: descTg,
                body: bodyTg,
            }).unwrap();
        }

        // медиа по очереди: фото → видео → аудио → фото рефки
        if (photoFile) {
            await uploadMedia({ id, kind: "photo", file: photoFile }).unwrap();
            setPhotoFile(null);
        }
        if (videoFile) {
            await uploadMedia({ id, kind: "video", file: videoFile }).unwrap();
            setVideoFile(null);
        }
        if (audioFile) {
            await uploadMedia({ id, kind: "audio", file: audioFile }).unwrap();
            setAudioFile(null);
        }
        if (refPhotoFile) {
            await uploadMedia({
                id,
                kind: "ref_photo",
                file: refPhotoFile,
            }).unwrap();
            setRefPhotoFile(null);
            if (localRefPhotoUrl?.startsWith("blob:"))
                URL.revokeObjectURL(localRefPhotoUrl);
            setLocalRefPhotoUrl(null);
        }

        await postsQ.refetch();
        return id!;
    };

    const saveDraft = async () => {
        setBusy(true);
        setErr(null);
        setFlash(null);
        try {
            await persist();
            setFlash("Сохранено");
        } catch (e: any) {
            setErr(e?.data?.message || e?.error || "Ошибка сохранения");
        } finally {
            setBusy(false);
        }
    };

    const publish = async () => {
        if (editingPublished) return;
        setBusy(true);
        setErr(null);
        setFlash(null);
        try {
            const id = await persist();
            const published = await publishPost(id).unwrap();
            setFlash(
                published.data.post_url
                    ? `Опубликовано: ${published.data.post_url}`
                    : "Опубликовано в @ALNY_art",
            );
            postsQ.refetch();
        } catch (e: any) {
            setErr(e?.data?.message || e?.error || "Ошибка публикации");
        } finally {
            setBusy(false);
        }
    };

    const sync = async () => {
        if (!editingId || !editingPublished) return;
        setBusy(true);
        setErr(null);
        setFlash(null);
        try {
            await persist();
            await syncPost(editingId).unwrap();
            setFlash("Подпись в канале обновлена (первое сообщение)");
            postsQ.refetch();
        } catch (e: any) {
            setErr(e?.data?.message || e?.error || "Ошибка обновления");
        } finally {
            setBusy(false);
        }
    };

    const aliasValue = (p: MusicChannelPost) =>
        aliasDrafts[p.id] ??
        (p.alias_message_ids && p.alias_message_ids.length
            ? p.alias_message_ids.join(", ")
            : "");

    const persistAliasIds = async (p: MusicChannelPost, ids: number[]) => {
        await updatePost({
            id: p.id,
            alias_message_ids: ids,
        }).unwrap();
        setAliasDrafts((prev) => {
            const next = { ...prev };
            delete next[p.id];
            return next;
        });
        await postsQ.refetch();
    };

    const saveAliases = async (p: MusicChannelPost) => {
        setBusy(true);
        setErr(null);
        setFlash(null);
        try {
            const raw = aliasValue(p).trim();
            // додати до вже існуючих (поле = нові id), або повна заміна якщо порожньо
            const existing = p.alias_message_ids || [];
            let next: number[];
            if (!raw) {
                next = existing;
                setFlash("Нечего сохранять — введи msg id или сними чипы ×");
                return;
            }
            const added = raw
                .split(/[,;\s]+/)
                .flatMap((part) => {
                    const range = part.match(/^(\d+)\s*[-–—]\s*(\d+)$/);
                    if (range) {
                        let a = Number(range[1]);
                        let b = Number(range[2]);
                        if (a > b) [a, b] = [b, a];
                        const out: number[] = [];
                        for (let i = a; i <= b && i - a <= 50; i++) out.push(i);
                        return out;
                    }
                    return /^\d+$/.test(part) ? [Number(part)] : [];
                })
                .filter((n) => n > 0 && n !== p.channel_message_id);
            next = Array.from(new Set(existing.concat(added))).sort(
                (a, b) => a - b,
            );
            await persistAliasIds(p, next);
            setFlash(
                `Привязано к #${p.id}: msg ${next.join(", ") || "—"} → рефка на ${
                    p.channel_message_id ?? "—"
                }`,
            );
        } catch (e: any) {
            setErr(
                e?.data?.message || e?.error || "Не удалось сохранить привязку",
            );
        } finally {
            setBusy(false);
        }
    };

    const removeAliasId = async (p: MusicChannelPost, mid: number) => {
        setBusy(true);
        setErr(null);
        setFlash(null);
        try {
            const next = (p.alias_message_ids || []).filter((x) => x !== mid);
            await persistAliasIds(p, next);
            setFlash(`Снята привязка msg ${mid} с #${p.id}`);
        } catch (e: any) {
            setErr(e?.data?.message || e?.error || "Не удалось снять привязку");
        } finally {
            setBusy(false);
        }
    };

    const runImport = async () => {
        setBusy(true);
        setErr(null);
        setFlash(null);
        try {
            const result = await importPosts({ limit: 40 }).unwrap();
            const d = result.data;
            setFlash(
                `Обновлено с канала: +${d.imported} новых, ${d.updated} обновлено`,
            );
            postsQ.refetch();
        } catch (e: any) {
            setErr(e?.data?.message || e?.error || "Ошибка обновления");
        } finally {
            setBusy(false);
        }
    };

    const hide = async (id: number) => {
        const p = history.find((x) => x.id === id);
        const label = p?.channel_message_id
            ? `TG ${p.channel_message_id}`
            : `черновик #${id}`;
        if (
            !window.confirm(
                `Спрятать ${label} из Истории? В канале @ALNY_art пост останется.`,
            )
        )
            return;
        setBusy(true);
        setErr(null);
        setFlash(null);
        try {
            await hidePost(id).unwrap();
            if (editingId === id) resetForm();
            setFlash(`Спрятан ${label} (канал не тронут)`);
            postsQ.refetch();
        } catch (e: any) {
            setErr(e?.data?.message || "Ошибка скрытия");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id: number) => {
        const p = history.find((x) => x.id === id);
        const label = p?.channel_message_id
            ? `TG ${p.channel_message_id}` +
              ((p.alias_message_ids?.length ?? 0) > 0
                  ? ` + привязки ${p.alias_message_ids!.join(",")}`
                  : "")
            : `черновик #${id}`;
        if (
            !window.confirm(
                `Удалить ${label} из истории И из канала @ALNY_art?`,
            )
        )
            return;
        setBusy(true);
        setErr(null);
        setFlash(null);
        try {
            await deletePost(id).unwrap();
            if (editingId === id) resetForm();
            setFlash(`Удалён ${label} (история + канал)`);
            postsQ.refetch();
        } catch (e: any) {
            setErr(e?.data?.message || "Ошибка удаления");
        } finally {
            setBusy(false);
        }
    };

    const clearMedia = async (
        kind: "photo" | "video" | "audio" | "ref_photo",
    ) => {
        if (!editingId) {
            if (kind === "photo") pickPhoto(null);
            if (kind === "video") pickVideo(null);
            if (kind === "audio") pickAudio(null);
            if (kind === "ref_photo") pickRefPhoto(null);
            return;
        }
        setBusy(true);
        try {
            await uploadMedia({ id: editingId, kind, clear: true }).unwrap();
            if (kind === "photo") {
                setPhotoFile(null);
                if (localPhotoUrl?.startsWith("blob:"))
                    URL.revokeObjectURL(localPhotoUrl);
                setLocalPhotoUrl(null);
            }
            if (kind === "video") {
                setVideoFile(null);
                setVideoName(null);
            }
            if (kind === "audio") {
                setAudioFile(null);
                setAudioName(null);
            }
            if (kind === "ref_photo") {
                setRefPhotoFile(null);
                if (localRefPhotoUrl?.startsWith("blob:"))
                    URL.revokeObjectURL(localRefPhotoUrl);
                setLocalRefPhotoUrl(null);
            }
            postsQ.refetch();
        } catch (e: any) {
            setErr(e?.data?.message || "Ошибка очистки медиа");
        } finally {
            setBusy(false);
        }
    };

    const statsActive =
        statsSub === "users"
            ? usersQ
            : statsSub === "generated"
              ? linksQ
              : eventsQ;
    const statsTotal = statsActive.data?.total ?? 0;

    const tabs: { id: MainTab; label: string }[] = [
        { id: "post", label: "Пост" },
        { id: "history", label: "История" },
        { id: "stats", label: "Статистика" },
    ];

    const canPublish =
        Boolean(
            title.trim() ||
            telegramHtmlToText(descHtml) ||
            telegramHtmlToText(bodyHtml),
        ) ||
        Boolean(
            hasCoverPreview ||
            previewVideoName ||
            previewAudioName ||
            photoFile,
        );

    return (
        <div className="space-y-5 p-4 md:p-6">
            <style>{`
                .ProseMirror { outline: none; min-height: 140px; }
                .ProseMirror a { color: #2563eb; text-decoration: underline; }
                .ProseMirror p { margin: 0.25em 0; }
                .tg-preview b, .tg-preview strong { font-weight: 700; }
                .tg-preview i, .tg-preview em { font-style: italic; }
                .tg-preview u { text-decoration: underline; }
                .tg-preview s { text-decoration: line-through; }
                .tg-preview a { color: #6ab3f3; text-decoration: underline; }
            `}</style>

            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Музыка
                </h1>
                <p className="text-sm text-gray-500">
                    Постинг в @ALNY_art · реф-бот @alny_music_bot
                </p>
            </div>

            <div className="flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-white p-1.5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                            setMainTab(t.id);
                            setOffset(0);
                        }}
                        className={`min-w-[120px] flex-1 rounded-md px-4 py-2.5 text-sm font-semibold ${
                            mainTab === t.id
                                ? "bg-blue-600 text-white shadow"
                                : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {(flash || err) && (
                <div
                    className={`rounded-lg border px-4 py-3 text-sm ${
                        err
                            ? "border-red-200 bg-red-50 text-red-700"
                            : "border-emerald-200 bg-emerald-50 text-emerald-800"
                    }`}
                >
                    {err || flash}
                </div>
            )}

            {mainTab === "post" && (
                <div className="space-y-5">
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                        {editingId ? (
                            <>
                                {editingPost?.channel_message_id ? (
                                    <span className="rounded-md bg-sky-600 px-2.5 py-1 font-mono text-sm font-bold text-white">
                                        TG {editingPost.channel_message_id}
                                    </span>
                                ) : (
                                    <span className="rounded-md bg-amber-500 px-2.5 py-1 text-sm font-bold text-white">
                                        черновик
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="text-gray-500 underline"
                                >
                                    Новый пост
                                </button>
                                {editingPost?.post_url ? (
                                    <a
                                        href={editingPost.post_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="ml-auto text-blue-600 hover:underline"
                                    >
                                        Открыть в TG →
                                    </a>
                                ) : null}
                            </>
                        ) : (
                            <span>Новый пост · фото → видео → аудио</span>
                        )}
                    </div>

                    {/* КАРТКА 1: канал — медиа + подпись */}
                    <div className="overflow-hidden rounded-2xl border-2 border-sky-400/70 bg-sky-50/40 shadow-sm dark:border-sky-700 dark:bg-sky-950/20">
                        <div className="border-b border-sky-200 bg-sky-100/80 px-4 py-3 dark:border-sky-800 dark:bg-sky-900/40">
                            <div className="text-sm font-bold text-sky-950 dark:text-sky-100">
                                Пост в канал @ALNY_art
                            </div>
                            <div className="text-[11px] text-sky-700 dark:text-sky-300">
                                медиа + подпись · превью справа
                            </div>
                        </div>
                        <div className="grid gap-5 p-4 xl:grid-cols-[minmax(0,1fr)_minmax(420px,1fr)]">
                            <div className="space-y-3">
                                <div className="rounded-lg border border-sky-300 bg-white p-3 dark:border-sky-800 dark:bg-gray-900">
                                    <div className="mb-2 text-sm font-semibold">
                                        ① Фото
                                    </div>
                                    <CoverSlot
                                        src={previewPhoto}
                                        postId={coverPostId}
                                        className="mb-2 max-h-32 rounded object-cover"
                                    />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) =>
                                            pickPhoto(
                                                e.target.files?.[0] || null,
                                            )
                                        }
                                    />
                                    {(hasCoverPreview || photoFile) && (
                                        <button
                                            type="button"
                                            className="mt-1 text-xs text-red-600"
                                            onClick={() => clearMedia("photo")}
                                        >
                                            убрать фото
                                        </button>
                                    )}
                                </div>
                                <div className="rounded-lg border border-sky-300 bg-white p-3 dark:border-sky-800 dark:bg-gray-900">
                                    <div className="mb-2 text-sm font-semibold">
                                        ② Видео
                                    </div>
                                    {previewVideoName ? (
                                        <div className="mb-1 text-xs text-gray-500">
                                            {previewVideoName}
                                        </div>
                                    ) : null}
                                    <input
                                        type="file"
                                        accept="video/*"
                                        onChange={(e) =>
                                            pickVideo(
                                                e.target.files?.[0] || null,
                                            )
                                        }
                                    />
                                    {(previewVideoName || videoFile) && (
                                        <button
                                            type="button"
                                            className="mt-1 text-xs text-red-600"
                                            onClick={() => clearMedia("video")}
                                        >
                                            убрать видео
                                        </button>
                                    )}
                                </div>
                                <div className="rounded-lg border border-sky-300 bg-white p-3 dark:border-sky-800 dark:bg-gray-900">
                                    <div className="mb-2 text-sm font-semibold">
                                        ③ Аудио
                                    </div>
                                    {previewAudioName ? (
                                        <div className="mb-1 text-xs text-gray-500">
                                            {previewAudioName}
                                        </div>
                                    ) : null}
                                    <input
                                        type="file"
                                        accept="audio/*"
                                        onChange={(e) =>
                                            pickAudio(
                                                e.target.files?.[0] || null,
                                            )
                                        }
                                    />
                                    {(previewAudioName || audioFile) && (
                                        <button
                                            type="button"
                                            className="mt-1 text-xs text-red-600"
                                            onClick={() => clearMedia("audio")}
                                        >
                                            убрать аудио
                                        </button>
                                    )}
                                </div>

                                <div>
                                    <div className="mb-1 text-sm font-medium">
                                        Подпись / текст в канале
                                    </div>
                                    <div className="overflow-hidden rounded-md border border-sky-300 bg-white dark:border-sky-800 dark:bg-gray-900">
                                        <Toolbar editor={bodyEditor} />
                                        <div className="px-3 py-2">
                                            <EditorContent
                                                editor={bodyEditor}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-1 flex justify-between text-xs text-gray-500">
                                        <span>
                                            {channelLimits.chars}/
                                            {channelLimits.limit} ·
                                            форматирование{" "}
                                            {channelLimits.entities}/
                                            {TG_ENTITY_LIMIT}
                                        </span>
                                        {channelLimits.overChars && (
                                            <span className="text-red-600">
                                                лимит!
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {!editingPublished ? (
                                            <button
                                                type="button"
                                                disabled={busy || !canPublish}
                                                onClick={publish}
                                                className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                                            >
                                                Опубликовать пост
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                disabled={busy}
                                                onClick={sync}
                                                className="rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                                            >
                                                Обновить подпись в канале
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={saveDraft}
                                            className="rounded-lg border border-sky-600 bg-white px-4 py-2.5 text-sm font-semibold text-sky-800 hover:bg-sky-50 disabled:opacity-50 dark:bg-gray-900 dark:text-sky-200"
                                        >
                                            Сохранить
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-sky-700">
                                    Превью канала
                                </div>
                                <ChannelPreview
                                    bodyHtml={bodyHtml}
                                    photoUrl={previewPhoto}
                                    coverPostId={coverPostId}
                                    videoName={previewVideoName}
                                    audioName={previewAudioName}
                                />
                            </div>
                        </div>
                    </div>

                    {/* КАРТКА 2: реф — заголовок + контент */}
                    <div className="overflow-hidden rounded-2xl border-2 border-violet-400/70 bg-violet-50/40 shadow-sm dark:border-violet-700 dark:bg-violet-950/20">
                        <div className="border-b border-violet-200 bg-violet-100/80 px-4 py-3 dark:border-violet-800 dark:bg-violet-900/40">
                            <div className="text-sm font-bold text-violet-950 dark:text-violet-100">
                                Реф-бот @alny_music_bot
                            </div>
                            <div className="text-[11px] text-violet-700 dark:text-violet-300">
                                заголовок + контент · превью справа · аудио msg
                                → История
                            </div>
                        </div>
                        <div className="grid gap-5 p-4 xl:grid-cols-[minmax(0,1fr)_minmax(420px,1fr)]">
                            <div className="space-y-3">
                                <div className="rounded-lg border border-violet-300 bg-white p-3 dark:border-violet-800 dark:bg-gray-900">
                                    <div className="mb-1 text-sm font-semibold">
                                        Фото для рефки
                                    </div>
                                    <p className="mb-2 text-[11px] text-violet-700/80 dark:text-violet-300/80">
                                        Если задано — бот шлёт это фото вместо
                                        видео/фото из канала. Иначе — копия
                                        поста из канала.
                                    </p>
                                    <CoverSlot
                                        src={refPreviewPhoto}
                                        postId={refCoverPostId}
                                        refCover
                                        className="mb-2 max-h-32 rounded object-cover"
                                        fallback={
                                            <div className="mb-2 flex h-16 items-center justify-center rounded bg-violet-50 text-[11px] text-violet-500 dark:bg-violet-950/40">
                                                из канала (видео/фото)
                                            </div>
                                        }
                                    />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) =>
                                            pickRefPhoto(
                                                e.target.files?.[0] || null,
                                            )
                                        }
                                    />
                                    {(hasRefCoverPreview || refPhotoFile) && (
                                        <button
                                            type="button"
                                            className="mt-1 text-xs text-red-600"
                                            onClick={() =>
                                                clearMedia("ref_photo")
                                            }
                                        >
                                            убрать фото рефки
                                        </button>
                                    )}
                                </div>
                                <label className="block text-sm">
                                    <span className="mb-1 block font-medium">
                                        Заголовок
                                    </span>
                                    <input
                                        value={title}
                                        onChange={(e) =>
                                            setTitle(e.target.value)
                                        }
                                        className="w-full rounded-lg border border-violet-300 bg-white px-3 py-2.5 dark:border-violet-800 dark:bg-gray-900"
                                    />
                                </label>
                                <div>
                                    <div className="mb-1 text-sm font-medium">
                                        Контент
                                    </div>
                                    <div className="overflow-hidden rounded-md border border-violet-300 bg-white dark:border-violet-800 dark:bg-gray-900">
                                        <Toolbar editor={descEditor} />
                                        <div className="px-3 py-2">
                                            <EditorContent
                                                editor={descEditor}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-1 flex justify-between text-xs text-gray-500">
                                        <span>
                                            {refLimits.chars}/{refLimits.limit}{" "}
                                            · форматирование{" "}
                                            {refLimits.entities}/
                                            {TG_ENTITY_LIMIT}
                                        </span>
                                        {refLimits.overChars && (
                                            <span className="text-red-600">
                                                лимит!
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-2 text-xs text-violet-700/80">
                                        В рефку: заголовок + контент + кнопка
                                        «Перейти в канал» + фото рефки (если
                                        задано) или копия поста из канала.
                                    </p>
                                    <button
                                        type="button"
                                        disabled={busy}
                                        onClick={saveDraft}
                                        className="mt-3 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
                                    >
                                        Сохранить рефку
                                    </button>
                                </div>
                            </div>
                            <div>
                                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-violet-700">
                                    Превью рефки
                                </div>
                                <RefPreview
                                    title={title}
                                    contentHtml={descHtml}
                                    photoUrl={refPreviewPhoto}
                                    coverPostId={refCoverPostId}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {mainTab === "history" && (
                <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                История постов
                            </div>
                            <p className="text-xs text-gray-500">
                                Привяжи msg id аудио/видео из канала → рефка на
                                этот пост
                            </p>
                        </div>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={runImport}
                            className="rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
                        >
                            Обновить последние посты
                        </button>
                    </div>

                    {postsQ.isLoading ? (
                        <div className="text-sm text-gray-500">Загрузка…</div>
                    ) : history.length === 0 ? (
                        <div className="rounded-xl border border-dashed p-10 text-center text-gray-400">
                            Пусто — опубликуй пост или обнови последние с канала
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            {history.map((p) => {
                                const aliases = p.alias_message_ids || [];
                                return (
                                    <div
                                        key={p.id}
                                        className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:border-violet-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-900 dark:hover:border-violet-600"
                                    >
                                        <div className="flex gap-3.5 p-4">
                                            <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-gray-100 ring-1 ring-black/5 dark:bg-gray-800">
                                                {p.has_cover ||
                                                p.channel_message_id ? (
                                                    <AuthCover
                                                        postId={p.id}
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="flex h-full items-center justify-center text-xs text-gray-400">
                                                        нет фото
                                                    </div>
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    {p.channel_message_id ? (
                                                        <span className="rounded-md bg-sky-600 px-2 py-0.5 font-mono text-xs font-bold text-white">
                                                            TG{" "}
                                                            {
                                                                p.channel_message_id
                                                            }
                                                        </span>
                                                    ) : (
                                                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-100">
                                                            черновик
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="mt-1.5 line-clamp-2 text-[15px] font-semibold leading-snug text-gray-900 dark:text-white">
                                                    {p.title || "Без заголовка"}
                                                </div>
                                                <p className="mt-1 line-clamp-2 text-xs text-gray-500">
                                                    {telegramHtmlToText(
                                                        p.description ||
                                                            p.body ||
                                                            "",
                                                    ) || "—"}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="mt-auto space-y-2.5 border-t border-violet-100 bg-violet-50/50 px-4 py-3 dark:border-violet-900/50 dark:bg-violet-950/30">
                                            <div className="text-[11px] font-semibold uppercase tracking-wide text-violet-800 dark:text-violet-300">
                                                Привязать ID аудио / видео
                                            </div>
                                            {aliases.length > 0 ? (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {aliases.map((mid) => (
                                                        <span
                                                            key={mid}
                                                            className="inline-flex items-center gap-1 rounded-full border border-violet-300 bg-white pl-2.5 pr-1 py-1 font-mono text-xs font-semibold text-violet-900 shadow-sm dark:border-violet-700 dark:bg-gray-950 dark:text-violet-100"
                                                        >
                                                            msg {mid}
                                                            <button
                                                                type="button"
                                                                disabled={busy}
                                                                title="Снять привязку"
                                                                onClick={() =>
                                                                    void removeAliasId(
                                                                        p,
                                                                        mid,
                                                                    )
                                                                }
                                                                className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-[11px] font-bold text-red-700 hover:bg-red-500 hover:text-white disabled:opacity-50 dark:bg-red-950 dark:text-red-300"
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : null}
                                            <div className="flex flex-wrap items-center gap-2">
                                                <input
                                                    value={aliasValue(p)}
                                                    onChange={(e) =>
                                                        setAliasDrafts(
                                                            (prev) => ({
                                                                ...prev,
                                                                [p.id]: e.target
                                                                    .value,
                                                            }),
                                                        )
                                                    }
                                                    className="min-w-[9rem] flex-1 rounded-lg border-2 border-violet-300 bg-white px-3 py-2 font-mono text-sm dark:border-violet-700 dark:bg-gray-950"
                                                />
                                                <button
                                                    type="button"
                                                    disabled={busy}
                                                    onClick={() =>
                                                        void saveAliases(p)
                                                    }
                                                    className="rounded-lg bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 disabled:opacity-50"
                                                >
                                                    Привязать
                                                </button>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2 pt-1">
                                                <button
                                                    type="button"
                                                    onClick={() => loadPost(p)}
                                                    className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
                                                >
                                                    Править
                                                </button>
                                                {p.post_url ? (
                                                    <a
                                                        href={p.post_url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-950"
                                                    >
                                                        TG
                                                    </a>
                                                ) : null}
                                                <button
                                                    type="button"
                                                    disabled={busy}
                                                    onClick={() =>
                                                        void hide(p.id)
                                                    }
                                                    className="ml-auto rounded-lg border border-amber-400 bg-amber-50 px-3.5 py-2 text-sm font-semibold text-amber-900 shadow-sm hover:bg-amber-100 disabled:opacity-50 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-900"
                                                >
                                                    Спрятать
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={busy}
                                                    onClick={() =>
                                                        void remove(p.id)
                                                    }
                                                    className="rounded-lg border-2 border-red-300 bg-red-50 px-3.5 py-2 text-sm font-bold text-red-700 shadow-sm transition hover:border-red-500 hover:bg-red-600 hover:text-white disabled:opacity-50 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-600 dark:hover:text-white"
                                                >
                                                    Удалить пост
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {mainTab === "stats" && (
                <div className="space-y-5">
                    {overviewQ.isLoading && (
                        <div className="text-sm text-gray-500">Загрузка…</div>
                    )}

                    {overview && (
                        <div className="space-y-3">
                            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                Всего в боте
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                <StatCard
                                    label="Открывали бота"
                                    value={overview.users ?? 0}
                                    hint="нажали Start хотя бы раз"
                                />
                                <StatCard
                                    label="Генерировали ссылку"
                                    value={overview.makers ?? 0}
                                    hint="хотя бы одна реф-ссылка"
                                />
                                <StatCard
                                    label="Ссылок"
                                    value={overview.links ?? 0}
                                    hint="рефок в базе"
                                />
                                <StatCard
                                    label="Кликов"
                                    value={overview.clicks ?? 0}
                                    hint="переходы по реф-ссылкам"
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                        {(
                            [
                                ["day", "День"],
                                ["week", "Неделя"],
                                ["month", "Месяц"],
                                ["all", "Всё время"],
                            ] as const
                        ).map(([id, label]) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setStatsPeriod(id)}
                                className={`rounded-lg border px-3.5 py-2 text-sm font-medium ${
                                    statsPeriod === id
                                        ? "border-blue-600 bg-blue-600 text-white"
                                        : "border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {overview && (
                        <div className="space-y-3">
                            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                {statsPeriod === "day"
                                    ? "За сутки"
                                    : statsPeriod === "week"
                                      ? "За 7 дней"
                                      : statsPeriod === "month"
                                        ? "За 30 дней"
                                        : "За всё время"}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                {statsPeriod !== "all" && (
                                    <>
                                        <StatCard
                                            label="Впервые открыли бота"
                                            value={overview.users_period ?? 0}
                                            hint="новые люди в этом окне"
                                        />
                                        <StatCard
                                            label="Пользовались генератором"
                                            value={overview.makers_period ?? 0}
                                            hint="просили ссылку, в том числе повторно"
                                        />
                                        <StatCard
                                            label="Новых ссылок"
                                            value={overview.links_period ?? 0}
                                            hint="рефки, созданные в этом окне"
                                        />
                                    </>
                                )}
                                <StatCard
                                    label="Перешли по реф-ссылке"
                                    value={overview.referred ?? 0}
                                    hint="уникальные люди по чужой рефке"
                                />
                                <StatCard
                                    label="Из них в канале @ALNY_art"
                                    value={overview.in_channel ?? 0}
                                    hint={
                                        (overview.referred ?? 0) > 0
                                            ? `подписаны · ${Math.round(
                                                  ((overview.in_channel ?? 0) /
                                                      (overview.referred || 1)) *
                                                      100,
                                              )}%`
                                            : "подписаны на канал"
                                    }
                                    tone="green"
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                        {(
                            [
                                ["generated", "Кто генерировал ссылки"],
                                ["referrals", "Кто под кем пришёл"],
                                ["users", "Все пользователи"],
                            ] as const
                        ).map(([id, label]) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => {
                                    setStatsSub(id);
                                    setOffset(0);
                                }}
                                className={`rounded-lg border px-3.5 py-2 text-sm font-medium ${
                                    statsSub === id
                                        ? "border-indigo-600 bg-indigo-600 text-white"
                                        : "border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                        {statsActive.isLoading ? (
                            <div className="p-4 text-sm text-gray-500">
                                Загрузка…
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                {statsSub === "generated" && (
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-900">
                                            <tr>
                                                <th className="px-4 py-2.5">
                                                    Кто
                                                </th>
                                                <th className="px-4 py-2.5">
                                                    Пост
                                                </th>
                                                <th className="px-4 py-2.5">
                                                    Когда
                                                </th>
                                                <th className="px-4 py-2.5">
                                                    Клики
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(linksQ.data?.data || []).map(
                                                (l: any) => (
                                                    <tr
                                                        key={l.id}
                                                        className="border-t dark:border-gray-700"
                                                    >
                                                        <td className="px-4 py-2.5 font-medium">
                                                            {tgLabel({
                                                                username:
                                                                    l.owner_username,
                                                                first_name:
                                                                    l.owner_first_name,
                                                                tg_id: l.owner_tg_id,
                                                            })}
                                                        </td>
                                                        <td className="px-4 py-2.5">
                                                            {l.post_title ||
                                                                `msg ${l.message_id}`}
                                                        </td>
                                                        <td className="px-4 py-2.5 text-gray-500">
                                                            {fmtWhen(
                                                                l.created_at,
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2.5">
                                                            {l.clicks}
                                                        </td>
                                                    </tr>
                                                ),
                                            )}
                                        </tbody>
                                    </table>
                                )}
                                {statsSub === "referrals" && (
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-900">
                                            <tr>
                                                <th className="px-4 py-2.5">
                                                    Кто пришёл
                                                </th>
                                                <th className="px-4 py-2.5">
                                                    Под кем
                                                </th>
                                                <th className="px-4 py-2.5">
                                                    Когда
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(eventsQ.data?.data || []).map(
                                                (e: any) => (
                                                    <tr
                                                        key={e.id}
                                                        className="border-t dark:border-gray-700"
                                                    >
                                                        <td className="px-4 py-2.5 font-medium">
                                                            {tgLabel({
                                                                username:
                                                                    e.actor_username,
                                                                first_name:
                                                                    e.actor_first_name,
                                                                tg_id: e.actor_tg_id,
                                                            })}
                                                        </td>
                                                        <td className="px-4 py-2.5">
                                                            →{" "}
                                                            {tgLabel({
                                                                username:
                                                                    e.mentor_username,
                                                                first_name:
                                                                    e.mentor_first_name,
                                                                tg_id: e.mentor_tg_id,
                                                            })}
                                                        </td>
                                                        <td className="px-4 py-2.5 text-gray-500">
                                                            {fmtWhen(
                                                                e.created_at,
                                                            )}
                                                        </td>
                                                    </tr>
                                                ),
                                            )}
                                        </tbody>
                                    </table>
                                )}
                                {statsSub === "users" && (
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-900">
                                            <tr>
                                                <th className="px-4 py-2.5">
                                                    Пользователь
                                                </th>
                                                <th className="px-4 py-2.5">
                                                    Ментор
                                                </th>
                                                <th className="px-4 py-2.5">
                                                    Привёл
                                                </th>
                                                <th className="px-4 py-2.5">
                                                    Ссылок
                                                </th>
                                                <th className="px-4 py-2.5">
                                                    Клики
                                                </th>
                                                <th className="px-4 py-2.5">
                                                    С
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(usersQ.data?.data || []).map(
                                                (u: any) => (
                                                    <tr
                                                        key={u.tg_id}
                                                        className="border-t dark:border-gray-700"
                                                    >
                                                        <td className="px-4 py-2.5 font-medium">
                                                            {tgLabel(u)}
                                                        </td>
                                                        <td className="px-4 py-2.5">
                                                            {u.mentor_tg_id
                                                                ? tgLabel({
                                                                      username:
                                                                          u.mentor_username,
                                                                      tg_id: u.mentor_tg_id,
                                                                  })
                                                                : "—"}
                                                        </td>
                                                        <td className="px-4 py-2.5">
                                                            {u.referrals}
                                                        </td>
                                                        <td className="px-4 py-2.5">
                                                            {u.links ?? 0}
                                                        </td>
                                                        <td className="px-4 py-2.5">
                                                            {u.clicks ?? 0}
                                                        </td>
                                                        <td className="px-4 py-2.5 text-gray-500">
                                                            {fmtWhen(
                                                                u.created_at,
                                                            )}
                                                        </td>
                                                    </tr>
                                                ),
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}
                        <div className="flex items-center justify-between border-t px-4 py-3 text-sm dark:border-gray-700">
                            <span className="text-gray-500">
                                {statsTotal === 0
                                    ? "0"
                                    : `${offset + 1}–${Math.min(offset + limit, statsTotal)} / ${statsTotal}`}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    disabled={offset <= 0}
                                    onClick={() =>
                                        setOffset(Math.max(0, offset - limit))
                                    }
                                    className="rounded border px-3 py-1 disabled:opacity-40"
                                >
                                    ←
                                </button>
                                <button
                                    type="button"
                                    disabled={offset + limit >= statsTotal}
                                    onClick={() => setOffset(offset + limit)}
                                    className="rounded border px-3 py-1 disabled:opacity-40"
                                >
                                    →
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MusicBotPage;
